import {customElement, property, queryAll, state} from "lit/decorators.js";
import Styles from "./accordion.lit.scss";
import {resolve} from "../../../container";
import {ScrollService} from "../../../common/scroll";

import {AutoInitializing} from "../../../common/elements";
import {ManagingResources} from "../../../common/lifetime";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import {intRangeClosed} from "../../../bootstrap/common/arrays";
import {ResizeObserverFactory} from "../../../common/observation";
import {noop} from "../../../common/utils/functions";
import {TrackingEventType} from "../../../tracking/types";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingAccordionInteract} from "../../../tracking/trackingAttributes";

//has to be the same duration as expansion animation in scss
export const EXPANSION_DURATION = 500;
export const NEGATIVE_MARGIN = 32;
export const HEADLINE_CONTENT_GAP = 16;

@customElement("eop-accordion")
export class EopAccordion extends AutoInitializing(ManagingResources(LitElement)) {

    public static readonly styles = Styles;

    @property({attribute: "accordion-config"})
    public config: string;
    @state()
    private toggleStates: { [id: number]: TrackingEventType } = {};
    @queryAll(".accordion-item")
    private items: NodeListOf<HTMLElement>;

    private allowOnlyOneOpened: boolean;
    private itemCount: number;
    private verticalAligned: boolean;
    private layoutElement: Element;
    private previousLayoutElementHeight: number;
    private resizeObserver: ResizeObserver;

    public constructor(
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private scrollService: ScrollService = resolve(ScrollService)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.allowOnlyOneOpened = this.config === "single";
        this.itemCount = this.querySelectorAll(".accordion-item-content").length;
        this.resizeObserver = this.resizeObserverFactory.create(() => this.updateHeights());
        this.verticalAligned = !!this.closestThat(e => e.classList.contains("vertical-align"));
        this.layoutElement = this.closestThat(e => e.classList.contains("layout-element"))!;
    }

    public render(): TemplateResult {
        return html`
            <div class="accordion">${this.renderItems()}</div>
        `;
    }

    public renderItems(): TemplateResult[] {
        return intRangeClosed(1, this.itemCount).map(id => this.renderItem(id));
    }

    public renderItem(id: number): TemplateResult {
        const handleToggle = (e: Event): void => this.toggle(e, id);
        const label = this.getTrackingLabel(id);
        return html`
            <div class="accordion-item">
                <div tabindex="0"
                     class="tabbable accordion-item-headline-container"
                     @click=${handleToggle}
                     @keydown=${handleToggle}
                     ${trackingAccordionInteract(label, this.toggleStates[id] ?? TrackingEventType.CLOSE)}
                >
                    <slot name="accordion-item-headline-${id}"></slot>
                </div>
                <div class="accordion-item-content-container">
                    <slot name="accordion-item-content-${id}"></slot>
                </div>
            </div>
            ${this.renderSeparator(id)}
        `;
    }

    private renderSeparator(id: number): Nullable<TemplateResult> {
        if (id < this.itemCount) {
            return html`
                <div class="separator-line"></div>`;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.updateHeights();
        this.items.forEach(item => this.resizeObserver.observe(this.assignedItemForAccordionItem(item)));
    }

    private getTrackingLabel(id: number): string {
        const itemText = this.querySelector(`[slot=accordion-item-headline-${id}]`)!.textContent.trim();
        return itemText.length > 0 ? itemText : `accordion item ${id}`;
    }

    private updateHeights(): void {
        this.items.forEach(item => this.setCalculatedHeight(item));
    }

    private setCalculatedHeight(item: HTMLElement): void {
        const height = this.assignedItemForAccordionItem(item).clientHeight;
        item.style.setProperty("--collapsible-height", `${height}px`);
    }

    private assignedItemForAccordionItem(item: HTMLElement): HTMLElement {
        return item.querySelector<HTMLSlotElement>(".accordion-item-content-container slot")!.assignedElements()[0] as HTMLElement;
    }

    private toggle(e: Event, id: number): void {
        if (e instanceof KeyboardEvent && e.key !== " " && e.key !== "Enter" && e.key !== "Escape") {
            return;
        }

        const toggledItem = this.items.item(id - 1);
        const open = toggledItem.hasAttribute("open");
        this.previousLayoutElementHeight = this.layoutElement.clientHeight;

        if (open) {
            this.close(toggledItem);
            this.toggleStates = {...this.toggleStates, [id]: TrackingEventType.CLOSE};
        } else {
            const previouslyOpenedItem = this.getPreviouslyOpenedItem(toggledItem);
            if (previouslyOpenedItem && this.allowOnlyOneOpened) {
                this.openItemAndCloseOther(toggledItem, previouslyOpenedItem);
            } else {
                this.open(toggledItem);
            }
            this.toggleStates = {...this.toggleStates, [id]: TrackingEventType.OPEN};
        }
    }

    private close(toggledItem: HTMLElement): void {
        toggledItem.removeAttribute("open");
        this.scrollToItem(toggledItem, this.offsetFromVerticalAlignmentWhenClosing(toggledItem));
    }

    private openItemAndCloseOther(toggledItem: HTMLElement, previouslyOpenedItem: HTMLElement): void {
        previouslyOpenedItem.removeAttribute("open");
        toggledItem.setAttribute("open", "");

        this.scrollToItem(toggledItem,
            this.calculateClosedMarginBetween(toggledItem, previouslyOpenedItem) +
            this.offsetFromVerticalAlignmentWhenClosing(previouslyOpenedItem) +
            this.offsetFromVerticalAlignmentWhenOpening(toggledItem));
    }

    private open(toggledItem: HTMLElement): void {
        toggledItem.setAttribute("open", "");
        this.scrollToItem(toggledItem, this.offsetFromVerticalAlignmentWhenOpening(toggledItem));
    }

    private getPreviouslyOpenedItem(openingItem: HTMLElement): Nullable<HTMLElement> {
        return Array.from(this.items)
                .findFirst(item => item.hasAttribute("open") && item !== openingItem)
            ?? null;
    }

    private calculateClosedMarginBetween(openingItem: HTMLElement, closingItem: HTMLElement): number {
        if (this.openingItemBeforeClosingItem(openingItem, closingItem)) {
            return closingItem.style.getPropertyValue("--collapsible-height").toInt()
                + HEADLINE_CONTENT_GAP;
        }
        return 0;
    }

    private openingItemBeforeClosingItem(openingItem: HTMLElement, closingItem: HTMLElement): boolean {
        return closingItem.compareDocumentPosition(openingItem) === this.DOCUMENT_POSITION_FOLLOWING;
    }

    private offsetFromVerticalAlignmentWhenOpening(toggledItem: HTMLElement): number {
        return this.currentlyVerticalAligned() ? 0.5 * this.getHeight(toggledItem) : 0;
    }

    private offsetFromVerticalAlignmentWhenClosing(toggledItem: HTMLElement): number {
        return this.currentlyVerticalAligned() ? -0.5 * this.getHeight(toggledItem) : 0;
    }

    private currentlyVerticalAligned(): boolean {
        if (!this.verticalAligned) {
            return false;
        }
        return this.computedStyle().getPropertyValue("--grid-columns").toInt() !== 12;
    }

    private getHeight(item: HTMLElement): number {
        return item.style.getPropertyValue("--collapsible-height").toInt() + HEADLINE_CONTENT_GAP;
    }

    private scrollToItem(item: HTMLElement, closedMargin: number): void {
        void this.scrollService.scrollToElement(item, closedMargin + NEGATIVE_MARGIN, EXPANSION_DURATION, EXPANSION_DURATION)
            .then(this.compensateLayoutBodyHeightChange());
    }

    private compensateLayoutBodyHeightChange() {
        if (this.currentlyVerticalAligned()) {
            return () => this.scrollService.scrollRelative(0.5 * (this.layoutElement.clientHeight - this.previousLayoutElementHeight), EXPANSION_DURATION);
        } else {
            return () => noop();
        }
    }
}